import { useCallback, useMemo, useState, type JSX } from "react";
import { SearchRegular, ArrowSyncRegular, DocumentTextRegular, ChevronRightRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { useApi } from "@/hooks/useApi";
import { TasksApi } from "@/services/api/tasks-api";
import type { TriyoTask } from "@/types/models";
import { formatNumericCalendarDate } from "@/utils/calendar-date";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import "../shared.scss";
import "./Tasks.scss";

type TaskBucket = "active" | "archived" | "completed";

interface TaskGroup {
  id: TaskBucket;
  label: string;
  accent: string;
  countLabel: (n: number) => string;
}

const TASK_GROUPS: TaskGroup[] = [
  {
    id: "active",
    label: "Standard Tasks",
    accent: "#0E75BB",
    countLabel: (n) => `${n} Active Task${n === 1 ? "" : "s"}`,
  },
  {
    id: "archived",
    label: "Other Tasks",
    accent: "#808080",
    countLabel: (n) => `${n} Archived Task${n === 1 ? "" : "s"}`,
  },
  {
    id: "completed",
    label: "Completed Tasks",
    accent: "#1D7D2C",
    countLabel: (n) => `${n} Completed Task${n === 1 ? "" : "s"}`,
  },
];

const GROUP_PREVIEW_COUNT = 4;

const STATUS_BADGES: Record<string, { bg: string; fg: string; label: string }> = {
  in_progress: { bg: "#F7EBFF", fg: "#7405C0", label: "In Progress" },
  not_started: { bg: "#F0F0F0", fg: "#555555", label: "Not Started" },
  overdue: { bg: "#FCE9E2", fg: "#C03E06", label: "Overdue" },
  completed: { bg: "#EBF5ED", fg: "#045912", label: "Completed" },
  archived: { bg: "#F0F0F0", fg: "#555555", label: "Archived" },
};

function taskTitle(task: TriyoTask): string {
  return task.Name || task.Title || task.TaskName || task.Subject || "Untitled task";
}

function taskStatusKey(task: TriyoTask): string {
  if (task.IsOverdue) return "overdue";
  if (task.IsCompleted) return "completed";
  if (task.IsArchived) return "archived";
  const raw = (task.StatusName || task.TaskStatus || task.Status || "").toLowerCase();
  if (raw.includes("complete") || raw.includes("done") || raw.includes("closed")) return "completed";
  if (raw.includes("archive")) return "archived";
  if (raw.includes("overdue") || raw.includes("late")) return "overdue";
  if (raw.includes("progress") || raw.includes("active") || raw.includes("open")) return "in_progress";
  if (raw.includes("not") || raw.includes("new") || raw.includes("pending")) return "not_started";
  return "not_started";
}

function taskBucket(task: TriyoTask): TaskBucket {
  const key = taskStatusKey(task);
  if (key === "completed") return "completed";
  if (key === "archived") return "archived";
  return "active";
}

function formatDueDate(task: TriyoTask): string {
  const raw = task.DueDate || task.EndDate;
  if (!raw) return "—";
  return formatNumericCalendarDate(raw);
}

function StatusBadge({ statusKey }: { statusKey: string }): JSX.Element {
  const palette = STATUS_BADGES[statusKey] ?? STATUS_BADGES.not_started;
  return (
    <span className="task-status" style={{ background: palette.bg, color: palette.fg }}>
      {palette.label}
    </span>
  );
}

export function Tasks(): JSX.Element {
  const fetcher = useCallback(() => TasksApi.getUserTasks(), []);
  const { data, loading, error, refetch } = useApi(fetcher);
  const [query, setQuery] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const filtered = useMemo(() => {
    const list = data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((t) => {
      const title = taskTitle(t).toLowerCase();
      const project = (t.ProjectName || t.Project || "").toLowerCase();
      return title.includes(q) || project.includes(q);
    });
  }, [data, query]);

  const groups = TASK_GROUPS.map((group) => ({
    group,
    items: filtered.filter((t) => taskBucket(t) === group.id),
  })).filter(({ items }) => items.length > 0);

  const refresh = async () => {
    setSpinning(true);
    try {
      await refetch();
    } finally {
      setSpinning(false);
    }
  };

  const tasksUrl = resolveLaunchUrl("tasks");

  return (
    <div className="tab-panel">
      <TabSectionHeader
        title="Tasks"
        action={
          <button
            type="button"
            aria-label="Refresh"
            title="Refresh"
            className={`icon-button${spinning ? " icon-button--spin" : ""}`}
            disabled={spinning}
            aria-busy={spinning}
            onClick={() => void refresh()}
          >
            <ArrowSyncRegular />
          </button>
        }
      />

      <div className="search-row">
        <div className="search-field">
          <SearchRegular />
          <input
            className="search-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Tasks"
            aria-label="Search for Tasks"
          />
        </div>
      </div>

      <div className="list-scroll">
        {loading ? (
          <div className="empty-state" role="status" aria-live="polite">
            <FluentSpinner label="Loading tasks…" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="empty-state" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && groups.length === 0 ? (
          <div className="empty-state" role="status">
            No Tasks{query ? " match your search" : ""}
          </div>
        ) : null}

        {!loading &&
          !error &&
          groups.map(({ group, items }) => {
            const isExpanded = !!expanded[group.id];
            const visible = isExpanded ? items : items.slice(0, GROUP_PREVIEW_COUNT);
            const listId = `task-group-${group.id}`;
            return (
              <div key={group.id} className="task-group">
                <div className="task-group__header">
                  <div className="task-group__header-body">
                    <span className="task-group__accent" style={{ background: group.accent }} />
                    <span className="task-group__labels">
                      <span className="task-group__name">{group.label}</span>
                      <span className="task-group__count">{group.countLabel(items.length)}</span>
                    </span>
                    {items.length > GROUP_PREVIEW_COUNT ? (
                      <button
                        type="button"
                        className="task-group__toggle"
                        aria-expanded={isExpanded}
                        aria-controls={listId}
                        onClick={() => setExpanded((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                      >
                        {isExpanded ? "Show fewer tasks" : "View all tasks"}
                        <ChevronRightRegular className={isExpanded ? "task-group__chevron--open" : undefined} />
                      </button>
                    ) : null}
                  </div>
                  <span className="list-row__divider" />
                </div>

                <div id={listId}>
                  {visible.map((task) => {
                    const statusKey = taskStatusKey(task);
                    return (
                      <div key={task.Id ?? task.TaskId ?? taskTitle(task)} className="task-row">
                        <div className="task-row__body">
                          <div className="task-row__left">
                            <span className="task-row__icon">
                              <DocumentTextRegular />
                            </span>
                            <span className="task-row__text">
                              <span className="task-row__title">{taskTitle(task)}</span>
                              <span className="task-row__due">Due Date: {formatDueDate(task)}</span>
                            </span>
                          </div>
                          <div className="task-row__right">
                            {statusKey === "overdue" ? (
                              <span className="task-row__action" title="Action required">
                                !
                              </span>
                            ) : null}
                            <StatusBadge statusKey={statusKey} />
                          </div>
                        </div>
                        <span className="list-row__divider" />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
      </div>

      {tasksUrl ? (
        <div className="tasks-footer">
          <a className="tasks-footer__link" href={tasksUrl} target="_blank" rel="noreferrer">
            Switch to web application
          </a>
        </div>
      ) : null}
    </div>
  );
}
